import { resetPasswordSchema } from "@reelio/contracts";
import { Check } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";

import { ApiRequestError, resetPassword } from "./api";
import { AuthShell } from "./AuthShell";
import { Field, PrimaryButton } from "./FormControls";

export const ResetPasswordPage = () => {
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<{
    token?: string;
    password?: string;
    confirmPassword?: string;
  }>({});
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = resetPasswordSchema.safeParse({
      token,
      password,
      confirmPassword,
    });

    if (!parsed.success) {
      const fieldErrors = parsed.error.flatten().fieldErrors;
      setErrors({
        token: fieldErrors.token?.[0],
        password: fieldErrors.password?.[0],
        confirmPassword: fieldErrors.confirmPassword?.[0],
      });
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    setFormError("");
    try {
      await resetPassword({
        token: parsed.data.token,
        password: parsed.data.password,
      });
      setIsComplete(true);
    } catch (error) {
      setFormError(
        error instanceof ApiRequestError
          ? error.message
          : "We could not reset the password. Request a new link.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthShell
      title={isComplete ? "Password updated" : "Choose a new password"}
      description={
        isComplete
          ? "Your old sessions have been signed out."
          : "Use at least 10 characters with uppercase, lowercase and a number."
      }
      footer={
        isComplete ? null : (
          <p>
            Need another link?{" "}
            <Link to="/auth/forgot-password">Request one</Link>
          </p>
        )
      }
    >
      {isComplete ? (
        <section className="auth-success" aria-live="polite">
          <span className="auth-success__icon" aria-hidden="true">
            <Check size={21} />
          </span>
          <h2>You can sign in now.</h2>
          <p>Your new password is active.</p>
          <Link className="auth-submit" to="/auth/login">
            Log in
          </Link>
        </section>
      ) : token ? (
        <form className="auth-form" onSubmit={submit} noValidate>
          <Field
            label="New password"
            name="password"
            type="password"
            autoComplete="new-password"
            value={password}
            error={errors.password}
            onChange={(event) => {
              setPassword(event.target.value);
              setErrors((current) => ({ ...current, password: undefined }));
              setFormError("");
            }}
          />
          <Field
            label="Confirm new password"
            name="confirmPassword"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            error={errors.confirmPassword}
            onChange={(event) => {
              setConfirmPassword(event.target.value);
              setErrors((current) => ({
                ...current,
                confirmPassword: undefined,
              }));
              setFormError("");
            }}
          />

          {formError ? (
            <p className="auth-form__alert" role="alert">
              {formError}
            </p>
          ) : null}

          <PrimaryButton
            type="submit"
            isLoading={isSubmitting}
            loadingLabel="Updating password…"
          >
            Update password
          </PrimaryButton>
        </form>
      ) : (
        <section className="auth-success" role="alert">
          <h2>This reset link is incomplete.</h2>
          <p>Request a fresh password reset email and use its full link.</p>
          <Link className="auth-secondary" to="/auth/forgot-password">
            Request another link
          </Link>
        </section>
      )}
    </AuthShell>
  );
};
